import { DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE } from "./../utility/enums/common";
import { ApplicationManageData, NheDetails } from "./../utility/application-data";
import {
  ADDITIONAL_VALID_APP_STATE_TO_DISPLAY_NHE_UK,
  EMPLOYMENT_TYPE,
  UK_EMPLOYMENT_TYPE_LABEL_OVERRIDE,
} from "./../config/appConfigConstants";
import moment from "moment";
import { CAAS_STATUS } from "../components/i9-task/i9-data";
import {
  COUNTRIES_BACKGROUND_CHECK_ONLY,
  COUNTRIES_WITHOUT_I9,
  COUNTRIES_WITH_VIRTUAL_NHE,
  NOT_TENTATIVE_START_APPLICATION_STATES,
  JOB_DETAIL_DATE_FORMAT,
  JOB_DETAIL_TIME_FORMAT,
  LAUNCHED_COUNTRY,
  RIGHT_TO_WORK_TASK_DEFINITION_ID,
  RIGHT_TO_WORK_TASK_TITLE,
  SDA_DATE_FORMAT,
  SDA_TIME_FORMAT,
  NOT_TENTATIVE_START_SF_STEP_SUBSTEP,
  SHOW_SCREENING_RESULT_VALID_STATES,
  SHOW_SCREENING_RESULT_VALID_STATES_UK,
} from "../config/appConfigConstants";
import { getTaskDefinitionsFromConfig } from "../utility/app-config-helper";
import {
  JobDetailsModel,
  SdaInfo,
  getApplicationManageData,
  getApplicationManageDataFromSalesforce,
} from "../utility/application-data";
import * as Cookie from "js-cookie";
import { ArbBundle } from "../hooks/use-arb";
import { isSalesforceApplication } from "./url-params";
import { APPLICATION_STATE, SF_APP_STATE } from "../utility/constants/application-status";
import { NHEAppointmentStatusEnum } from "../components/appointment-card/card-controlled";
import { StatusEnum } from "../hoc/with-status";
import { DOC_UPLOAD_ITEM_WIDGET_VALID_STATE, EDM_REVIEW_STATUS, EDM_UPLOAD_STATUS } from "../utility/enums/common";
import { CommitUploadRequest, DocumentWidget } from "../utility/types/common";
import { getRegionFromCookie } from "../utility/locale-helper";
import { sdaAccessorCountries } from "../utility/constants";

export const resolveDurationText = (unit: string, status?: string, duration?: number) => {
  if (status && isCaasStatus(status)) {
    if (status == CAAS_STATUS.AwaitingCandidateInfo) {
      return duration && unit;
    }

    return "";
  }

  return duration && unit;
};

const isCaasStatus = (status: string): boolean => {
  return Object.values(CAAS_STATUS).includes(status as CAAS_STATUS);
};

export const resolveSdaResponse = async (
  applicationId: string,
  jobDetail: JobDetailsModel | undefined,
  sdaAccessor: () => Promise<SdaInfo | undefined>
): Promise<SdaInfo | undefined> => {
  const currentRegion = getRegionFromCookie() as LAUNCHED_COUNTRY;

  if (sdaAccessorCountries.includes(currentRegion)) {
    return await sdaAccessor();
  }

  if (jobDetail && isRtwTaskDefinitionConfigEnabled(applicationId)) {
    return setupSdaPayloadFromJobDetail(jobDetail);
  }

  return undefined;
};

export const isRtwTaskDefinitionConfigEnabled = (applicationId: string): boolean => {
  const taskDefinitionConfig = getTaskDefinitionsFromConfig(RIGHT_TO_WORK_TASK_DEFINITION_ID, applicationId);

  return taskDefinitionConfig?.taskTitle === RIGHT_TO_WORK_TASK_TITLE;
};

export const setupSdaPayloadFromJobDetail = (jobDetail: JobDetailsModel): SdaInfo => {
  return {
    startDate: convertJobDetailDateToSdaDate(jobDetail.firstDayOnSite),
    startTime: convertJobDetailTimeToSdaTime(jobDetail.startTime),
    publicText: jobDetail.publicText || "",
    address: jobDetail.address,
    startDateStatus: jobDetail.startDateStatus || "",
    timezone: jobDetail.timezone || "",
  };
};

export const convertJobDetailDateToSdaDate = (jobDetailDate?: string): string => {
  if (!jobDetailDate) {
    return "";
  }

  return moment(jobDetailDate, JOB_DETAIL_DATE_FORMAT).format(SDA_DATE_FORMAT);
};

export const convertJobDetailTimeToSdaTime = (jobDetailTime?: string): string => {
  if (!jobDetailTime) {
    return "";
  }

  return moment(jobDetailTime, JOB_DETAIL_TIME_FORMAT).format(SDA_TIME_FORMAT);
};

export const getCurrentLocale = () => {
  const userDefinedLocale = Cookie.get("hvh-locale");
  const DEFAULT_LOCALE = "en-US";

  return userDefinedLocale && userDefinedLocale.length > 0 ? userDefinedLocale : DEFAULT_LOCALE;
};

export const isCurrentLocale = (locale: string) => getCurrentLocale() === locale;

export const isCountryWithBackgroundCheckOnly = (country: LAUNCHED_COUNTRY) => {
  return country in COUNTRIES_BACKGROUND_CHECK_ONLY;
};

export const isCountryWithoutI9 = (country: LAUNCHED_COUNTRY) => {
  return country in COUNTRIES_WITHOUT_I9;
};

export const isCountryWithVirtualNHE = (country: LAUNCHED_COUNTRY) => {
  return country in COUNTRIES_WITH_VIRTUAL_NHE;
};

// Will receive a string separated by comma, with the assumption the first index as the chime link
export const getChimeLinkForVirtualNHE = (input: string, placeholder: string) => {
  const _input = input.split(",");

  return _input[0] && _input[0].includes("https://chime.aws") ? _input[0] : placeholder;
};

export const showStartDateTooltip = (bundle: ArbBundle, country: LAUNCHED_COUNTRY) => {
  if (isCountryWithBackgroundCheckOnly(country) && country === LAUNCHED_COUNTRY.GB)
    return bundle.getMessage("Checklist-PossibleStartDate-ToolTip-UK");

  if (isCountryWithBackgroundCheckOnly(country))
    return bundle.getMessage("Checklist-StartDate-BackgroundCheckOnly-ToolTip");

  return bundle.getMessage("Checklist-StartDate-ToolTip");
};

export const getApplicationState = async (applicationId: string) => {
  return !isSalesforceApplication(applicationId)
    ? await getApplicationManageData(applicationId)
    : await getApplicationManageDataFromSalesforce(applicationId);
};

export const isTentativeStartDateByApplicationState = async (
  applicationId: string,
  sdaDetails: SdaInfo | undefined
) => {
  const { applicationState, applicationStepSubStep } = await getApplicationState(applicationId);
  const isSDAStartDateTentative = sdaDetails?.startDateStatus === "TENTATIVE";

  // possible null if applicationState is come from SalesForce
  if (applicationState) {
    const isApplicationStateTentative = !NOT_TENTATIVE_START_APPLICATION_STATES.includes(
      applicationState as APPLICATION_STATE
    );
    return isApplicationStateTentative && isSDAStartDateTentative;
  } else if (applicationStepSubStep) {
    const isApplicationStateTentative = !NOT_TENTATIVE_START_SF_STEP_SUBSTEP.includes(
      applicationStepSubStep as SF_APP_STATE
    );
    return isApplicationStateTentative && isSDAStartDateTentative;
  }

  return isSDAStartDateTentative;
};

export const overrideEmploymentType = (country: LAUNCHED_COUNTRY, value: EMPLOYMENT_TYPE) => {
  switch (country) {
    case LAUNCHED_COUNTRY.GB:
      return value in UK_EMPLOYMENT_TYPE_LABEL_OVERRIDE ? UK_EMPLOYMENT_TYPE_LABEL_OVERRIDE[value] : value;
    default:
      return value;
  }
};

type showEmploymentTypeProps = {
  bundle: ArbBundle;
  country: LAUNCHED_COUNTRY;
  employmentType: EMPLOYMENT_TYPE;
  employmentTypeL10N: string;
};

export const showEmploymentType = ({
  bundle,
  country,
  employmentType,
  employmentTypeL10N,
}: showEmploymentTypeProps) => {
  if (country === LAUNCHED_COUNTRY.GB) {
    const translatedEmploymentType = bundle.getMessage(
      `Checklist-Common-${overrideEmploymentType(country, employmentType)}`
    );
    return translatedEmploymentType != "N/A" ? translatedEmploymentType : employmentTypeL10N;
  }

  return employmentTypeL10N;
};

export const getValidStatesForShowScreeningResult = (country: LAUNCHED_COUNTRY) => {
  switch (country) {
    case LAUNCHED_COUNTRY.GB:
      return [...SHOW_SCREENING_RESULT_VALID_STATES, ...SHOW_SCREENING_RESULT_VALID_STATES_UK] as string[];
    default:
      return SHOW_SCREENING_RESULT_VALID_STATES as string[];
  }
};

type isEligibleToDisplayNheProps = {
  country: LAUNCHED_COUNTRY;
  nheDetails: NheDetails;
  applicationManageDetails: ApplicationManageData | undefined;
};

export const isEligibleToDisplayNhe = ({
  nheDetails,
  applicationManageDetails,
  country,
}: isEligibleToDisplayNheProps) => {
  const isValid = !(
    nheDetails?.inPast ||
    (applicationManageDetails?.applicationState && nheDetails?.status !== NHEAppointmentStatusEnum.RESERVED) ||
    nheDetails?.status === NHEAppointmentStatusEnum.COMPLETED
  );

  switch (country) {
    case LAUNCHED_COUNTRY.GB:
      if (applicationManageDetails?.applicationState) {
        return (
          isValid || ADDITIONAL_VALID_APP_STATE_TO_DISPLAY_NHE_UK.includes(applicationManageDetails?.applicationState)
        );
      }

      return isValid;

    default:
      return isValid;
  }
};

export const getStatusOverride = <T extends StatusEnum | CAAS_STATUS | string>(
  shouldOverrideStatus: Record<string, T> | undefined,
  status: T | undefined
) => {
  return status && shouldOverrideStatus && shouldOverrideStatus[status] ? shouldOverrideStatus[status] : status;
};

type isEligibleToShowLanguageSupportedOnJobDetailsProps = {
  country: LAUNCHED_COUNTRY;
  isCustomerService: boolean;
  supportedLanguage: string | undefined;
};

export const isEligibleToShowLanguageSupportedOnJobDetails = ({
  country,
  isCustomerService,
  supportedLanguage,
}: isEligibleToShowLanguageSupportedOnJobDetailsProps) => {
  const isHasSupportedLanguage = supportedLanguage && supportedLanguage.length > 0;

  switch (country) {
    case LAUNCHED_COUNTRY.MX:
      return false;
    case LAUNCHED_COUNTRY.GB:
      if (isCustomerService) return false;
      return isHasSupportedLanguage;
    default:
      return isHasSupportedLanguage;
  }
};

export const getDocumentWidgetState = (uploadStatus: EDM_UPLOAD_STATUS) => {
  switch (uploadStatus) {
    case EDM_UPLOAD_STATUS.IN_PROGRESS:
    case EDM_UPLOAD_STATUS.PENDING:
      return DOC_UPLOAD_ITEM_WIDGET_VALID_STATE.PROCESSING;
    case EDM_UPLOAD_STATUS.SUCCEEDED:
      return DOC_UPLOAD_ITEM_WIDGET_VALID_STATE.SUCCESSFUL;
    case EDM_UPLOAD_STATUS.FAILED:
      return DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE.FAILED_UPLOAD;
    default:
      throw new Error("Should return a valid state");
  }
};

export const isApprovedByEDM = (reviewStatus: EDM_REVIEW_STATUS | undefined) => {
  switch (reviewStatus) {
    case EDM_REVIEW_STATUS.APPROVED:
    case EDM_REVIEW_STATUS.APPROVED_MANUALLY_UPLOADED:
      return true;
    default:
      return false;
  }
};

export const getValidEdmPreCommitPayload = (docs: DocumentWidget[]) => {
  const documents: CommitUploadRequest = {
    documentsMetadata: docs
      .filter((doc) => !doc.markForDeletion && !isApprovedByEDM(doc.reviewStatus))
      .map((doc) => ({
        document_type_id: doc.documentTypeId,
        document_id: doc.documentId,
        draft_id: doc.draftId,
      })),
  };

  return documents;
};
